import React from "react";
import MrclText from "../marcel/MrclText";
import styled from "styled-components";
import { formatPercentage } from "../../utils/numberUtils";
import { COLORS } from "../../utils/colors";

type DeltaBadgeProps = {
  value: number | null | undefined;
  suffix?: string;
  showSign?: boolean;
  showIcon?: boolean;
  showSignWithIcon?: boolean;
  size?: "small" | "medium";
  formatter?: (value: number) => string;
  status?: "success" | "error" | "warning" | "default" | "info";
};

const DeltaBadge = ({
  value,
  suffix = "%",
  showSign = true,
  showIcon = true,
  showSignWithIcon = false,
  size = "small",
  formatter,
  status,
}: DeltaBadgeProps) => {
  if (value === null || value === undefined || isNaN(value)) {
    return <span>-</span>;
  }

  const variant = status
    ? (status === "success" ? "positive" : status === "error" ? "negative" : "neutral")
    : (value > 0 ? "positive" : value < 0 ? "negative" : "neutral");

  // Si l'icône est affichée, elle indique déjà le sens de l'évolution : on n'affiche pas les signes +/-
  const sign = showIcon && !showSignWithIcon ? "" : showSign && value > 0 ? "+" : value < 0 ? "-" : "";
  const formattedValue = formatter
    ? formatter(Math.abs(value))
    : formatPercentage(Math.abs(value), suffix).replace(",", ".");
  const variantColor =
    variant === "positive" ? COLORS.green : variant === "negative" ? COLORS.red : "#8a6100";

  return (
    <Badge $variant={variant} $size={size}>
      {showIcon && (
        <TrendIcon $variant={variant} aria-hidden="true" viewBox="0 0 16 16">
        {variant === "positive" && (
        <path d="M2.5 11 6 7.5 8.5 10 13.5 5M10 5h3.5v3.5" />
       )}

        {variant === "negative" && (
         <path d="M2.5 5.5 6 9 8.5 6.5 13.5 11.5M10 11.5h3.5V8" />
        )}

        {variant === "neutral" && (
        <path d="M2.5 8h11M10.5 5 13.5 8l-3 3" />
         )}
       </TrendIcon>
       )}

      <Value size="s" bold style={{ color: variantColor }}>
        {sign}
        {formattedValue}
      </Value>
    </Badge>
  );
};

export default DeltaBadge;

const Badge = styled.span<{
  $variant: "positive" | "negative" | "neutral";
  $size: "small" | "medium";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $size }) => ($size === "medium" ? "4px" : "2px")};
  padding: ${({ $size }) => ($size === "medium" ? "8px 12px" : "2px 6px")};
  min-width: 65px;
  min-height: ${({ $size }) => ($size === "medium" ? "auto" : "24px")};
  border-radius: 999px;
  width: fit-content;
  white-space: nowrap;

  background-color: ${({ $variant }) =>
    $variant === "positive"
      ? COLORS.bg_green
      : $variant === "negative"
      ? COLORS.bg_red
      : "#fff7e0"};

  color: ${({ $variant }) =>
    $variant === "positive"
      ? COLORS.green
      : $variant === "negative"
      ? COLORS.red
      : "#8a6100"};
`;

const TrendIcon = styled.svg<{
  $variant: "positive" | "negative" | "neutral";
}>`
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const Value = styled(MrclText)`
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 20px;
`;
